'use client';

import { useState, type MouseEvent, type ReactNode } from 'react';
import { HandHeart, MapPin, ChevronRight, Users } from 'lucide-react';
import type { FinderOpportunity } from './types';
import { campusLabel, campusShort, fmtClock, fmtDay, fmtTime, parseMp } from './format';
import { toPlainText } from './richText';
import { CampusIcon } from './CampusIcon';
import { Tag } from './chips';

interface ServeCardProps {
  opportunity: FinderOpportunity;
  /** Opportunity_ID is appended */
  detailsUrl: string;
  /**
   * Opens the opportunity in a sheet (the finder). When omitted the whole
   * card is a link to the details page (search results and other listings).
   */
  onOpen?: (opportunity: FinderOpportunity) => void;
}

const TAG_ICON = 'h-3.5 w-3.5 text-gray-500';

/**
 * Art for a serve card: the opportunity's image, else its ministry's, else a
 * brand tile. A file MP lists but cannot serve (it happens) falls back to
 * the tile too, rather than leaving a blank box.
 */
export function ServeArt({
  opportunity,
  className = '',
}: {
  opportunity: FinderOpportunity;
  className?: string;
}) {
  const [broken, setBroken] = useState<string | null>(null);
  if (opportunity.imageUrl && broken !== opportunity.imageUrl) {
    return (
      <img
        src={opportunity.imageUrl}
        alt=""
        loading="lazy"
        decoding="async"
        onError={() => setBroken(opportunity.imageUrl)}
        className={`h-full w-full object-cover ${className}`}
        draggable={false}
      />
    );
  }
  return (
    <div
      className={`flex h-full w-full items-center justify-center ${className}`}
      style={{ background: 'linear-gradient(135deg, #1c2b39 0%, #243848 50%, #2d4a5c 100%)' }}
    >
      <HandHeart className="h-12 w-12 text-[#4cb8ab]" strokeWidth={1.5} />
    </div>
  );
}

/**
 * "9:30 AM – 12:30 PM", "9:30 AM", or null when there is no shift. No shift
 * times but a date with a time of day: that time, ending its duration later
 * (Day of Hope is dated 1 PM for 4 hours: "1 PM – 5 PM").
 */
export function serveShift(o: FinderOpportunity): string | null {
  if (!o.shiftStart) {
    const start = o.date ? parseMp(o.date) : null;
    if (!start || (start.getHours() === 0 && start.getMinutes() === 0)) return null;
    if (!o.durationHours) return fmtTime(start);
    const end = new Date(start.getTime() + o.durationHours * 3_600_000);
    return `${fmtTime(start)} – ${fmtTime(end)}`;
  }
  return o.shiftEnd
    ? `${fmtClock(o.shiftStart)} – ${fmtClock(o.shiftEnd)}`
    : fmtClock(o.shiftStart);
}

/**
 * "Sat, Sep 12 · 9:30 AM – 12:30 PM" for a one-time opportunity, the shift
 * alone for an ongoing one with set hours, and nothing for a plain ongoing
 * role: the absence of a date says "ongoing" without cluttering the card.
 */
export function serveWhen(o: FinderOpportunity): string | null {
  const shift = serveShift(o);
  if (o.date) {
    const day = fmtDay(parseMp(o.date));
    return shift ? `${day} · ${shift}` : day;
  }
  return shift;
}

/** "Ages 16+", "Women", "Men" */
export function serveRequirements(o: FinderOpportunity): string[] {
  const out: string[] = [];
  if (o.minimumAge) out.push(`Ages ${o.minimumAge}+`);
  if (o.requiredGender)
    out.push(
      o.requiredGender === 'Female'
        ? 'Women'
        : o.requiredGender === 'Male'
          ? 'Men'
          : o.requiredGender
    );
  return out;
}

export function ServeTags({
  opportunity: o,
  children,
}: {
  opportunity: FinderOpportunity;
  /** Extra tags after the standard ones (the details view adds its own) */
  children?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      <Tag icon={<CampusIcon congregationId={o.congregationId} className="h-4 w-4" />}>
        {campusShort(o.congregationName)}
      </Tag>
      {o.ministryName && <Tag>{o.ministryName}</Tag>}
      {serveRequirements(o).map((r) => (
        <Tag key={r}>{r}</Tag>
      ))}
      {o.remaining !== null && (
        <Tag icon={<Users className={TAG_ICON} />}>
          {o.remaining === 0
            ? 'Team is full'
            : o.remaining === 1
              ? '1 spot left'
              : `${o.remaining} spots left`}
        </Tag>
      )}
      {children}
    </div>
  );
}

const CARD =
  'group flex h-full cursor-pointer flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white text-left text-inherit no-underline shadow-sm transition-all hover:scale-[1.01] hover:shadow-md active:scale-[0.995]';

/**
 * An opportunity's page. A path-style details URL (".../serve/") gets the
 * trailing slash WordPress would redirect to anyway, so the click lands in one
 * hop and never on a browser-cached redirect from before /serve/{id} existed.
 * A query-style one ("...?id=") is left as it is.
 */
export function serveHref(detailsUrl: string, id: number): string {
  return detailsUrl.endsWith('/') ? `${detailsUrl}${id}/` : `${detailsUrl}${id}`;
}

/**
 * Where a Collection lives: /serve/{slug} on the same site the details page is
 * on. A Collection without a slug falls back to its details page by id.
 */
export function collectionHref(o: FinderOpportunity, detailsUrl: string): string {
  if (!o.slug) return serveHref(detailsUrl, o.id);
  try {
    return `${new URL(detailsUrl).origin}/serve/${o.slug}/`;
  } catch {
    return `/serve/${o.slug}/`;
  }
}

export function ServeCard({ opportunity: o, detailsUrl, onOpen }: ServeCardProps) {
  const summary = toPlainText(o.description).trim();
  const when = serveWhen(o);
  const href = o.isCollection
    ? collectionHref(o, detailsUrl)
    : o.externalRegistrationUrl || serveHref(detailsUrl, o.id);
  const cta = o.isCollection ? 'Ways to help' : 'Sign up';
  // A Collection is a page, not a sign-up: the card is always a link to it.
  const openHere = o.isCollection ? undefined : onOpen;

  function stop(e: MouseEvent) {
    e.stopPropagation();
  }

  const content = (
    <>
      <div className="relative aspect-video w-full overflow-hidden bg-gray-100">
        <ServeArt
          opportunity={o}
          className="transition-transform duration-500 group-hover:scale-[1.04]"
        />
      </div>

      <div className="flex flex-1 flex-col gap-3 p-5">
        {when && (
          <p className="text-xs font-bold tracking-widest text-[#299c8f] uppercase">{when}</p>
        )}
        <h3 className="text-lg leading-tight font-extrabold tracking-tight text-[#1c2b39] uppercase">
          {o.title}
        </h3>
        {summary && <p className="line-clamp-2 text-sm leading-relaxed text-gray-600">{summary}</p>}
        <div className="flex items-center gap-1.5 text-sm text-gray-600">
          <MapPin className="h-4 w-4 shrink-0 text-gray-400" />
          <span className="truncate">{campusLabel(o.congregationId, o.congregationName)}</span>
        </div>

        <div className="mt-auto flex flex-col gap-3 pt-1">
          <ServeTags opportunity={o} />
          <div className="flex items-center justify-end">
            {openHere ? (
              <a
                href={href}
                onClick={stop}
                className="inline-flex shrink-0 items-center gap-1 rounded-full bg-[#299c8f] px-4 py-2 text-sm font-bold text-white no-underline shadow-sm transition-all hover:scale-105 hover:shadow-md active:scale-95"
              >
                {cta}
                <ChevronRight className="h-4 w-4" strokeWidth={2.5} />
              </a>
            ) : (
              <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-[#299c8f] px-4 py-2 text-sm font-bold text-white shadow-sm transition-all group-hover:scale-105 group-hover:shadow-md">
                {cta}
                <ChevronRight className="h-4 w-4" strokeWidth={2.5} />
              </span>
            )}
          </div>
        </div>
      </div>
    </>
  );

  if (!openHere) {
    return (
      <a href={href} className={CARD}>
        {content}
      </a>
    );
  }

  return (
    <article
      role="button"
      tabIndex={0}
      onClick={() => openHere(o)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          openHere(o);
        }
      }}
      className={CARD}
    >
      {content}
    </article>
  );
}
